import { useState, useRef, useEffect, useCallback } from 'react'
import { useAuth } from './contexts/AuthContext'
import LoadingScreen from './components/LoadingScreen'
import LoginScreen from './components/LoginScreen'
import BottomTabs from './components/BottomTabs'
import MainNav from './components/MainNav'
import HomePage from './components/HomePage'
import PlayPage from './components/PlayPage'
import HistoryPage from './components/HistoryPage'
import MatchDashboard from './components/MatchDashboard'
import ActiveGame from './components/ActiveGame'
import GameHistory from './components/GameHistory'
import GameDetail from './components/GameDetail'
import Players from './components/Players'
import AddPlayersView from './components/AddPlayersView'
import QuickRoundSetup from './components/QuickRoundSetup'
import Leaderboard from './components/Leaderboard'
import Settlements from './components/Settlements'
import MoneyBoard from './components/MoneyBoard'
import SettleUp from './components/SettleUp'
import PlayerProfile from './components/PlayerProfile'
import ManageAccount from './components/ManageAccount'
import FollowsList from './components/FollowsList'
import GroupsList from './components/GroupsList'
import GroupDetail from './components/GroupDetail'
import TeamSetupPage from './components/TeamSetupPage'
import EventBuyInPage from './components/EventBuyInPage'
import BackfillResults from './components/BackfillResults'
import Ticker from './components/Ticker'
import ToastAlerts from './components/ToastAlerts'
import { useMatch as useMatchQuery } from './hooks/useMatches'
import { useMatchGames } from './hooks/useGames'
import { useRealtimeMatch } from './hooks/useRealtimeScores'
import { useQueryClient } from '@tanstack/react-query'
import { useMyPlayer, useAutoRefreshMyHandicap, useCreatePlayer } from './hooks/usePlayers'
import { extractUserNames } from './utils/userMeta'
import ErrorBoundary from './components/ErrorBoundary'
import logger from './utils/logger'
import { supabase } from './lib/supabase'
import { Menu, LogOut, X, Users, User, Mail, MessageSquare, UserStar } from 'lucide-react'
import FeedbackModal from './components/FeedbackModal'
import WelcomeModal, { hasSeenWelcome } from './components/WelcomeModal'
import UpdateModal from './components/UpdateModal'
import { useVersionCheck } from './hooks/useVersionCheck'
import { openExternalUrl } from './utils/openExternalUrl'
import { hideSplashScreen } from './lib/capacitorInit'
import { prefetchLocation } from './utils/geolocation'
import EmailVerificationGate from './components/EmailVerificationGate'

// Start GPS acquisition early so it's cached by the time the user reaches course search
prefetchLocation()

const DISCORD_INVITE = 'https://discord.gg/DpRjqBNE4D'

function DiscordIcon({ size = 16 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
      <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028c.462-.63.874-1.295 1.226-1.994a.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z" />
    </svg>
  )
}
import logoIcon from './assets/logo-icon-color.png'
import './App.css'

function saveWizardPhase(matchId, phase) {
  if (!matchId) return
  try {
    const key = `press_wizard_${matchId}`
    const raw = localStorage.getItem(key)
    const state = raw ? JSON.parse(raw) : {}
    localStorage.setItem(key, JSON.stringify({ ...state, phase, _v: 1, _ts: Date.now() }))
  } catch { /* ignore */ }
  logger.info('Wizard phase saved', { matchId, phase })
}

function clearWizardState(matchId) {
  if (!matchId) return
  try { localStorage.removeItem(`press_wizard_${matchId}`) } catch { /* ignore */ }
}

function App() {
  const { user, loading, playerLinking, sessionExpired, dismissSessionExpired, passwordRecovery, signOut } = useAuth()
  const { updateRequired, updateRecommended, storeUrl, dismissRecommendation } = useVersionCheck()
  const [activeMatchId, setActiveMatchId] = useState(() => {
    return localStorage.getItem('press_active_match') || null
  })
  const setupViewRestoredRef = useRef(false)
  const [currentView, setCurrentView] = useState(() => {
    const saved = localStorage.getItem('press_current_view')
    if (activeMatchId) {
      try {
        const raw = localStorage.getItem(`press_wizard_${activeMatchId}`)
        if (raw) {
          const parsed = JSON.parse(raw)
          if (parsed?.phase === 'team-setup') { setupViewRestoredRef.current = true; return 'team-setup' }
          if (parsed?.phase === 'event-buy-in') { setupViewRestoredRef.current = true; return 'event-buy-in' }
          if (parsed?.phase === 'session-wizard') return 'match'
          // round-setup wizard was open as a modal inside MatchDashboard — stay on 'match'
          // (the modal will re-open via showRoundWizard persistence)
          if (saved === 'match' || saved === 'rounds' || saved === 'leaderboard' || saved === 'money') return saved
          return 'quick-round-setup'
        }
      } catch { /* ignore */ }
    }
    if (saved === 'backfill') return saved
    if (saved && ['follows', 'profile', 'groups'].includes(saved)) return saved
    // Restore bottom-tab views (rounds, leaderboard, money) when an active match exists
    if (saved && activeMatchId && ['rounds', 'leaderboard', 'money'].includes(saved)) return saved
    return 'match'
  })
  const [mainTab, setMainTab] = useState(() => {
    return localStorage.getItem('press_main_tab') || 'home'
  })
  const [selectedGameId, setSelectedGameId] = useState(null)
  const [previousView, setPreviousView] = useState(null)
  const [sidebarReturnView, setSidebarReturnView] = useState(null)
  const [gameDetailInitialView, setGameDetailInitialView] = useState(null)
  const [scorecardHole, setScorecardHole] = useState(1)
  const [endRoundRequested, setEndRoundRequested] = useState(false)
  const [viewedSessionId, setViewedSessionId] = useState(null)
  const [viewedGameId, setViewedGameId] = useState(null)
  const [showSidebar, setShowSidebar] = useState(false)
  const [profilePlayerId, setProfilePlayerId] = useState(null)
  const [viewedGroupId, setViewedGroupId] = useState(null)
  const [isNewGroup, setIsNewGroup] = useState(false)
  const [showFeedbackModal, setShowFeedbackModal] = useState(false)
  const [showWelcome, setShowWelcome] = useState(false)
  const [dashboardWizardOpen, setDashboardWizardOpen] = useState(false)
  const [isEditingSetup, setIsEditingSetup] = useState(false)
  const [homeReady, setHomeReady] = useState(false)
  const [openSessionWizard, setOpenSessionWizard] = useState(() => {
    if (activeMatchId) {
      try {
        const raw = localStorage.getItem(`press_wizard_${activeMatchId}`)
        if (raw) {
          const parsed = JSON.parse(raw)
          return parsed?.phase === 'session-wizard'
        }
      } catch { /* ignore */ }
    }
    return false
  })
  const [sessionWizardReturnView, setSessionWizardReturnView] = useState(null)
  const sidebarRef = useRef(null)
  const appReadyRef = useRef(false)
  const appHeaderRef = useRef(null)

  // Track app-header height as a CSS variable for sticky positioning
  useEffect(() => {
    const el = appHeaderRef.current
    if (!el) return
    const ro = new ResizeObserver(() => {
      document.documentElement.style.setProperty('--app-header-height', `${el.offsetHeight}px`)
    })
    ro.observe(el)
    return () => ro.disconnect()
  }, [])

  // Persist active match
  useEffect(() => {
    if (activeMatchId) {
      localStorage.setItem('press_active_match', activeMatchId)
    } else {
      localStorage.removeItem('press_active_match')
    }
  }, [activeMatchId])

  // Persist active tab
  useEffect(() => {
    localStorage.setItem('press_main_tab', mainTab)
  }, [mainTab])

  // Persist current view (for standalone pages like profile/follows)
  useEffect(() => {
    localStorage.setItem('press_current_view', currentView)
  }, [currentView])

  // Reset dashboard wizard state when navigating away from match view
  useEffect(() => {
    if (currentView !== 'match') setDashboardWizardOpen(false)
  }, [currentView])

  // Close sidebar when tapping outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (sidebarRef.current && !sidebarRef.current.contains(e.target)) {
        setShowSidebar(false)
      }
    }
    if (showSidebar) {
      document.addEventListener('mousedown', handleClickOutside)
      document.addEventListener('touchstart', handleClickOutside)
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
      document.removeEventListener('touchstart', handleClickOutside)
    }
  }, [showSidebar])

  // Realtime subscriptions for match data
  useRealtimeMatch(activeMatchId)

  // Fetch the active match for type-aware labels (React Query deduplicates with MatchDashboard)
  // Suppress query while player linking is in progress to avoid RLS denial race condition
  const { data: activeMatch, isError: activeMatchError, error: activeMatchErrorObj } = useMatchQuery(playerLinking ? null : activeMatchId)

  // Clear stale activeMatchId only for definitive errors (match deleted, RLS denied).
  // Network errors should NOT kick the user out of their active scorecard.
  useEffect(() => {
    if (activeMatchError && activeMatchId && !playerLinking) {
      const msg = activeMatchErrorObj?.message || ''
      const isNotFound = msg.includes('not found') || msg.includes('access denied') ||
        activeMatchErrorObj?.code === 'PGRST116'
      if (isNotFound) {
        logger.warn('Active match not loadable, clearing stale reference: ' + activeMatchId)
        setActiveMatchId(null)
        setCurrentView('match')
        setMainTab('play')
        try { localStorage.removeItem(`press_wizard_${activeMatchId}`) } catch { /* ignore */ }
      }
    }
  }, [activeMatchError, activeMatchErrorObj, activeMatchId, playerLinking])

  // Check for active round (for ticker visibility)
  const { data: matchGames } = useMatchGames(activeMatchId)
  const hasActiveRound = matchGames?.some((g) => g.status === 'in_progress')

  // On page refresh, redirect quick-setup match types with no rounds to quick-round-setup
  // Skip if we restored into the session wizard flow (ref is stable — immune to state race)
  // Also skip if openSessionWizard is set — we're transitioning from buy-in to session wizard
  const hasRedirectedRef = useRef(openSessionWizard)
  useEffect(() => {
    if (hasRedirectedRef.current) return
    // Session wizard is opening — permanently suppress this redirect for the session
    if (openSessionWizard) {
      hasRedirectedRef.current = true
      return
    }
    // Once we've seen games or sessions, this match has been used — never redirect to setup
    if (matchGames?.length > 0 || activeMatch?.sessions?.length > 0) {
      hasRedirectedRef.current = true
      return
    }
    // Session-based match types (invitational, team_match) add sessions from the dashboard —
    // an active match with zero sessions/games is a valid state, not a setup redirect
    if (activeMatch?.match_type === 'invitational' || activeMatch?.match_type === 'team_match') {
      hasRedirectedRef.current = true
      return
    }
    if (
      activeMatch &&
      currentView === 'match' &&
      matchGames &&
      matchGames.length === 0 &&
      ['quick_round', 'match', 'trip'].includes(activeMatch.match_type) &&
      activeMatch.status === 'active'
    ) {
      hasRedirectedRef.current = true
      logger.warn('Redirecting no-games match to quick-round-setup', {
        matchId: activeMatchId,
        matchType: activeMatch.match_type,
      })
      setCurrentView('quick-round-setup')
    }
  }, [activeMatch, matchGames, openSessionWizard])

  // GUARD: Redirect already-activated events away from setup pages.
  // Only fires when the setup view was RESTORED from localStorage (stale wizard state
  // after hot reload / page refresh when isEditingSetup was lost).
  // Does NOT fire during live programmatic navigation (e.g., initial setup flow).
  useEffect(() => {
    if (!setupViewRestoredRef.current) return
    if (!activeMatch || !activeMatchId) return
    if (activeMatch.status !== 'active' && activeMatch.status !== 'completed') return
    const isSetupView = currentView === 'team-setup' || currentView === 'event-buy-in'
    if (!isSetupView) return
    if (isEditingSetup) return

    setupViewRestoredRef.current = false

    logger.capture(new Error('Activated event routed to setup page'), {
      matchId: activeMatchId,
      matchType: activeMatch.match_type,
      matchStatus: activeMatch.status,
      currentView,
      wizardPhase: (() => {
        try {
          const raw = localStorage.getItem(`press_wizard_${activeMatchId}`)
          return raw ? JSON.parse(raw)?.phase : null
        } catch { return 'parse-error' }
      })(),
    })

    clearWizardState(activeMatchId)
    setCurrentView('match')
  }, [activeMatch, activeMatchId, currentView, isEditingSetup])

  // Fetch the player record linked to this auth user
  const { data: myPlayer, isLoading: myPlayerLoading } = useMyPlayer()

  const isAdmin = myPlayer?.is_admin === true

  // Auto-refresh handicap from GHIN if stale (24h+)
  useAutoRefreshMyHandicap()

  const buildFeedbackContext = () => ({
    currentView,
    mainTab,
    activeMatchId,
    selectedGameId,
    url: window.location.href,
    userAgent: navigator.userAgent,
    platform: navigator.platform,
    screenSize: `${window.innerWidth}x${window.innerHeight}`,
    isNative: !!window.Capacitor?.isNativePlatform?.(),
    timestamp: new Date().toISOString(),
  })

  const createPlayerMut = useCreatePlayer()
  const [autoCreating, setAutoCreating] = useState(false)
  const autoCreatingGuard = useRef(false)
  const autoCreationAttempted = useRef(false)

  // Check if user has names in auth metadata (from sign-up or OAuth)
  const userHasAuthNames = (() => {
    if (!user) return false
    const { firstName, lastName } = extractUserNames(user)
    return !!firstName && !!lastName
  })()

  // Show loading screen while player record is being loaded/linked/created
  const pendingAutoCreate = !myPlayer && userHasAuthNames && !autoCreationAttempted.current
  const settingUp = !myPlayer && (playerLinking || myPlayerLoading || autoCreating || pendingAutoCreate)

  const profileIncomplete = !myPlayerLoading && (
    !myPlayer?.first_name || !myPlayer?.last_name ||
    myPlayer?.handicap_index == null || Number(myPlayer?.handicap_index) === 0
  )

  // Auto-create player record for new sign-ups that have names in auth metadata
  useEffect(() => {
    if (myPlayerLoading || !user || myPlayer || playerLinking || autoCreatingGuard.current) return
    const { firstName, lastName, displayName } = extractUserNames(user)
    if (!firstName || !lastName) return
    autoCreatingGuard.current = true
    autoCreationAttempted.current = true
    setAutoCreating(true)
    createPlayerMut.mutate({
      first_name: firstName,
      last_name: lastName,
      display_name: displayName || `${firstName} ${lastName}`,
      email: user.email,
      avatar_color: ['#0ea47a', '#5e9be6', '#f59e0b', '#ef4444', '#a78bfa', '#f472b6', '#fb923c', '#22d3ee'][Math.floor(Math.random() * 8)],
      user_id: user.id,
    }, {
      onSettled: () => { autoCreatingGuard.current = false; setAutoCreating(false) },
    })
  }, [myPlayerLoading, user, myPlayer, playerLinking])

  // Show welcome modal for first-time users after player setup completes
  useEffect(() => {
    if (!loading && user && myPlayer && !settingUp && !hasSeenWelcome()) {
      setShowWelcome(true)
    }
  }, [loading, user, myPlayer, settingUp])

  // Hide native splash screen once auth state is resolved
  useEffect(() => {
    if (!loading) {
      hideSplashScreen()
      // Guard against phantom taps during splash dismiss / login transition
      setTimeout(() => { appReadyRef.current = true }, 800)
    }
  }, [loading])

  // Watchdog B (5 s) + Watchdog L (1 s): App resume blank screen detection.
  // - Watchdog L fires fast at 1 s and inspects the rendered DOM via
  //   data-app-state on the root div, capturing the gate state at that moment.
  // - Watchdog B preserves the original 5 s "still loading" check.
  // Uses refs to read current state inside async timeouts without dependencies.
  const watchdogBState = useRef({})
  watchdogBState.current = { user, currentView, activeMatchId, playerLinking, settingUp, homeReady, mainTab, loading }
  useEffect(() => {
    const isNative = typeof window !== 'undefined' && window.Capacitor?.isNativePlatform?.()
    if (!isNative) return
    let bTimer = null
    let lTimer = null
    let listenerHandle = null
    ;(async () => {
      const { App: CapApp } = await import('@capacitor/app')
      listenerHandle = await CapApp.addListener('appStateChange', ({ isActive }) => {
        if (isActive && watchdogBState.current.user) {
          // Watchdog L — 1 s blank-DOM check
          lTimer = setTimeout(() => {
            const root = document.querySelector('.app')
            const appState = root?.getAttribute('data-app-state')
            const textLen = (document.body?.innerText || '').trim().length
            const looksBlank = !root || appState !== 'rendered' || textLen < 20
            if (looksBlank) {
              const s = watchdogBState.current
              logger.capture(new Error('Blank screen 1s after resume (Watchdog L)'), {
                appState, textLen,
                currentView: s.currentView, activeMatchId: s.activeMatchId,
                playerLinking: s.playerLinking, settingUp: s.settingUp,
                homeReady: s.homeReady, loading: s.loading, hasUser: !!s.user,
                isOnline: navigator.onLine,
              })
            }
          }, 1000)
          // Watchdog B — 5 s still-loading check
          bTimer = setTimeout(() => {
            const s = watchdogBState.current
            const homeLanding = !s.activeMatchId && (s.mainTab === 'home' || !s.mainTab) && s.currentView === 'match'
            const isStillLoading = s.loading || s.settingUp || (homeLanding && !s.homeReady)
            if (isStillLoading) {
              logger.capture(new Error('App resume: blank screen after 5s'), {
                currentView: s.currentView, activeMatchId: s.activeMatchId,
                playerLinking: s.playerLinking, hasUser: !!s.user,
                isOnline: navigator.onLine, settingUp: s.settingUp, homeReady: s.homeReady,
                loading: s.loading,
              })
            }
          }, 5000)
        } else {
          if (lTimer) { clearTimeout(lTimer); lTimer = null }
          if (bTimer) { clearTimeout(bTimer); bTimer = null }
        }
      })
    })().catch(() => {})
    return () => {
      if (lTimer) clearTimeout(lTimer)
      if (bTimer) clearTimeout(bTimer)
      if (listenerHandle) listenerHandle.remove()
    }
  }, [])

  // Watchdog F: Active match data lost detection
  const matchDataWasAvailable = useRef(false)
  const matchLostTimer = useRef(null)
  useEffect(() => {
    if (activeMatch && activeMatchId) {
      matchDataWasAvailable.current = true
      if (matchLostTimer.current) { clearTimeout(matchLostTimer.current); matchLostTimer.current = null }
    } else if (matchDataWasAvailable.current && activeMatchId && !activeMatch) {
      if (!matchLostTimer.current) {
        matchLostTimer.current = setTimeout(() => {
          logger.capture(new Error('Active match data lost for >5s'), {
            matchId: activeMatchId, isError: activeMatchError,
            errorMsg: activeMatchErrorObj?.message, isOnline: navigator.onLine,
          })
          matchLostTimer.current = null
        }, 5000)
      }
    }
    return () => { if (matchLostTimer.current) { clearTimeout(matchLostTimer.current); matchLostTimer.current = null } }
  }, [activeMatch, activeMatchId, activeMatchError, activeMatchErrorObj])

  // Watchdog J: Session wizard transition hijacked
  // Detects when openSessionWizard is set to true but never acknowledged by MatchDashboard
  // (e.g., redirect effect sends view to quick-round-setup before MatchDashboard mounts)
  useEffect(() => {
    if (!openSessionWizard) return
    const timer = setTimeout(() => {
      logger.capture(new Error('Session wizard transition not acknowledged within 5s'), {
        matchId: activeMatchId,
        currentView,
        matchType: activeMatch?.match_type,
        matchStatus: activeMatch?.status,
        sessions: activeMatch?.sessions?.length,
        gamesCount: matchGames?.length,
        isOnline: navigator.onLine,
      })
    }, 5000)
    return () => clearTimeout(timer)
  }, [openSessionWizard])

  // Watchdog K: Buy-in → match dashboard transition hijacked
  // Detects when completing buy-in navigates to 'match' but something redirects away within 5s
  const buyInCompletedRef = useRef(false)
  useEffect(() => {
    if (!buyInCompletedRef.current) return
    if (currentView !== 'match') {
      logger.capture(new Error('Buy-in to dashboard transition hijacked'), {
        matchId: activeMatchId,
        currentView,
        matchType: activeMatch?.match_type,
        matchStatus: activeMatch?.status,
        gamesCount: matchGames?.length,
        isOnline: navigator.onLine,
      })
      buyInCompletedRef.current = false
      return
    }
    const timer = setTimeout(() => {
      buyInCompletedRef.current = false
    }, 5000)
    return () => clearTimeout(timer)
  }, [currentView, buyInCompletedRef.current])

  // playerLinking gate breadcrumb (the blanking gate has been removed): logs
  // when player linking is active so any captured error during this window
  // shows the transition in its breadcrumb timeline. The match query at
  // useMatchQuery(playerLinking ? null : activeMatchId) already suppresses
  // network fetches during the link RPC, and cached match data renders normally.
  useEffect(() => {
    if (playerLinking && activeMatchId) {
      logger.auth('playerLinking active — rendering cached UI (gate removed)', { activeMatchId })
    }
  }, [playerLinking, activeMatchId])

  // Shared back-navigation handler used by both Android back button and iOS swipe-back.
  // Returns true if navigation was handled, false if we're at the top level (Android can minimize).
  const handleBackNavigation = () => {
    // Close sidebar first if open
    if (showSidebar) {
      setShowSidebar(false)
      return true
    }

    // Let open wizards/modals handle back first via custom event.
    // Any component can set detail.handled = true to claim the gesture.
    const evt = new CustomEvent('press:back-gesture', { detail: { handled: false } })
    window.dispatchEvent(evt)
    if (evt.detail.handled) return true

    // Navigate back based on current view
    switch (currentView) {
      case 'game-detail':
        setSelectedGameId(null)
        setCurrentView(previousView || 'match')
        setPreviousView(null)
        return true
      case 'settle-up':
        setCurrentView('money')
        return true
      case 'rounds':
      case 'leaderboard':
      case 'money':
        setViewedSessionId(null)
        setViewedGameId(null)
        setCurrentView('match')
        return true
      case 'players':
      case 'add-players':
        setCurrentView('match')
        return true
      case 'team-setup':
        if (isEditingSetup) {
          setIsEditingSetup(false)
          clearWizardState(activeMatchId)
          setCurrentView('match')
        } else {
          saveWizardPhase(activeMatchId, 'players')
          setCurrentView('quick-round-setup')
        }
        return true
      case 'event-buy-in':
        if (isEditingSetup) {
          setIsEditingSetup(false)
          clearWizardState(activeMatchId)
          setCurrentView('match')
        } else {
          saveWizardPhase(activeMatchId, 'team-setup')
          setCurrentView('team-setup')
        }
        return true
      case 'quick-round-setup':
        window.dispatchEvent(new CustomEvent('press:back-discard'))
        return true
      case 'profile':
        setCurrentView(previousView || 'match')
        setPreviousView(null)
        return true
      case 'manage-account':
        setCurrentView('profile')
        return true
      case 'follows':
        setCurrentView(sidebarReturnView || 'match')
        setSidebarReturnView(null)
        return true
      case 'groups':
        setCurrentView(sidebarReturnView || 'match')
        setSidebarReturnView(null)
        return true
      case 'group-detail':
        setViewedGroupId(null)
        setCurrentView('groups')
        return true
      case 'match':
        if (activeMatchId && activeMatch?.status === 'setup') {
          window.dispatchEvent(new CustomEvent('press:back-discard'))
          return true
        } else if (activeMatchId) {
          setActiveMatchId(null)
          setMainTab('play')
          return true
        } else if (mainTab !== 'home') {
          setMainTab('home')
          return true
        }
        return false // At top level — nothing to go back to
      default:
        return false
    }
  }

  // Android back button handler
  useEffect(() => {
    const isNative = typeof window !== 'undefined' &&
      window.Capacitor?.isNativePlatform?.()
    if (!isNative) return

    let listener
    ;(async () => {
      const { App: CapApp } = await import('@capacitor/app')
      listener = await CapApp.addListener('backButton', () => {
        if (!handleBackNavigation()) {
          CapApp.minimizeApp()
        }
      })
    })()

    return () => {
      if (listener) listener.remove()
    }
  }, [currentView, activeMatchId, activeMatch, showSidebar, previousView, sidebarReturnView, mainTab])

  // iOS swipe-back gesture support via native edge-swipe recognizer.
  // PressViewController.swift dispatches 'press:native-back' when the user
  // swipes from the left edge. We handle it with the same shared logic.
  useEffect(() => {
    const handler = () => handleBackNavigation()
    window.addEventListener('press:native-back', handler)
    return () => window.removeEventListener('press:native-back', handler)
  }, [currentView, activeMatchId, activeMatch, showSidebar, previousView, sidebarReturnView, mainTab])

  // Reset state and clear stale data when user signs out
  const queryClient = useQueryClient()
  useEffect(() => {
    if (!user) {
      setShowSidebar(false)
      setShowWelcome(false)
      autoCreationAttempted.current = false
      autoCreatingGuard.current = false
      setHomeReady(false)
      queryClient.clear()
    }
  }, [user])

  // Single loading overlay covers both auth/player setup AND initial home data loading.
  // By rendering it as an overlay (not an early return), HomePage mounts behind it
  // and its queries start immediately — no animation reset, no double spinner.
  const isHomeLanding = !activeMatchId && (mainTab === 'home' || !mainTab) && currentView === 'match'
  const showLoadingOverlay = user && (settingUp || (isHomeLanding && !homeReady))

  if (updateRequired) return <UpdateModal required storeUrl={storeUrl} />
  if (loading) {
    // Render-gate breadcrumb so Watchdog L / any captured error during this
    // window includes "auth-loading" in the timeline. Synchronous hydration
    // from readCachedSupabaseUser() avoids this gate when a session is cached.
    logger.auth('Render gated by auth loading (no cached session)')
    return <LoadingScreen />
  }
  if (!user || passwordRecovery) return <LoginScreen />

  // Enforce email verification for new accounts (created after cutoff).
  // Existing accounts created before this date keep the soft banner only.
  const EMAIL_VERIFY_CUTOFF = '2026-04-01T00:00:00Z'
  const isNewAccount = user.created_at && user.created_at >= EMAIL_VERIFY_CUTOFF
  const needsVerification = isNewAccount
    && !user.email_confirmed_at
    && user.app_metadata?.provider === 'email'
  if (needsVerification) return <EmailVerificationGate />

  const handleMatchSelect = (matchId, options) => {
    setActiveMatchId(matchId)
    if (options?.quickRoundSetup) {
      setCurrentView('quick-round-setup')
    } else {
      setCurrentView('match')
    }
  }

  const handleBackToMatches = () => {
    setActiveMatchId(null)
    setMainTab('play')
    setCurrentView('match')
  }

  const handleGoHome = () => {
    setActiveMatchId(null)
    setMainTab('home')
    setCurrentView('match')
  }

  const handleDeletedMatch = () => {
    setActiveMatchId(null)
    setMainTab('play')
    setCurrentView('match')
  }

  const handleSelectGame = (gameId) => {
    setViewedGameId(gameId)
  }

  const handleBackFromDetail = () => {
    setSelectedGameId(null)
    setCurrentView(previousView || 'match')
    setPreviousView(null)
  }

  const handleViewRound = (gameId) => {
    setViewedGameId(gameId)
    setCurrentView('rounds')
  }

  const handleStartRound = () => {
    setCurrentView('rounds')
  }

  const handleEndRound = () => {
    setEndRoundRequested(true)
    setCurrentView('rounds')
  }

  const handleManageRoster = () => {
    setCurrentView('players')
  }

  const handleAddPlayers = () => {
    setCurrentView('add-players')
  }

  const handleSetupTeams = (editing = false) => {
    if (editing) setIsEditingSetup(true)
    saveWizardPhase(activeMatchId, 'team-setup')
    setCurrentView('team-setup')
  }

  const handleViewProfile = (playerId) => {
    setProfilePlayerId(playerId || null)
    if (currentView !== 'profile') setPreviousView(currentView)
    setCurrentView('profile')
  }

  const renderView = () => {
    switch (currentView) {
      case 'backfill':
        return <BackfillResults />
      case 'match':
        if (!activeMatchId) {
          switch (mainTab) {
            case 'play':
              return <PlayPage onMatchSelect={handleMatchSelect} />
            case 'history':
              return <HistoryPage onMatchSelect={handleMatchSelect} />
            case 'home':
            default:
              return <HomePage onReady={() => setHomeReady(true)} onMatchSelect={handleMatchSelect} />
          }
        }
        return (
          <MatchDashboard
            matchId={activeMatchId}
            onBack={handleBackToMatches}
            onGoHome={handleGoHome}
            onDeletedMatch={handleDeletedMatch}
            onViewRound={handleViewRound}
            onStartRound={handleStartRound}
            onEndRound={handleEndRound}
            onManageRoster={handleManageRoster}
            onAddPlayers={handleAddPlayers}
            onViewProfile={handleViewProfile}
            onSetupTeams={handleSetupTeams}
            onSetupBuyIn={(editing = false) => {
              if (editing) setIsEditingSetup(true)
              saveWizardPhase(activeMatchId, 'event-buy-in')
              setCurrentView('event-buy-in')
            }}
            onNavigate={setCurrentView}
            onViewSession={(sessionId) => { setViewedSessionId(sessionId); setCurrentView('rounds') }}
            openSessionWizard={openSessionWizard}
            onSessionWizardOpened={() => setOpenSessionWizard(false)}
            onSessionWizardClosed={() => {
              if (sessionWizardReturnView) {
                saveWizardPhase(activeMatchId, sessionWizardReturnView)
                setCurrentView(sessionWizardReturnView)
                setSessionWizardReturnView(null)
              } else if (activeMatch?.status === 'active' || activeMatch?.status === 'completed') {
                // Active match — don't save a setup phase that would cause a stale redirect on reload
                clearWizardState(activeMatchId)
              } else {
                saveWizardPhase(activeMatchId, 'team-setup')
              }
            }}
            onSessionWizardCompleted={() => {
              clearWizardState(activeMatchId)
              setSessionWizardReturnView(null)
            }}
            onWizardToggle={setDashboardWizardOpen}
          />
        )
      case 'team-setup':
        return (
          <TeamSetupPage
            matchId={activeMatchId}
            matchType={activeMatch?.match_type}
            isEditing={isEditingSetup}
            onBack={() => {
              if (isEditingSetup) {
                setIsEditingSetup(false)
                clearWizardState(activeMatchId)
                setCurrentView('match')
              } else {
                saveWizardPhase(activeMatchId, 'players')
                setCurrentView('quick-round-setup')
              }
            }}
            onContinueToBuyIn={() => {
              saveWizardPhase(activeMatchId, 'event-buy-in')
              setCurrentView('event-buy-in')
            }}
          />
        )
      case 'event-buy-in':
        return (
          <EventBuyInPage
            matchId={activeMatchId}
            isEditing={isEditingSetup}
            onBack={() => {
              logger.info('EventBuyIn onBack', { matchType: activeMatch?.match_type, status: activeMatch?.status, isEditing: isEditingSetup })
              if (isEditingSetup) {
                setIsEditingSetup(false)
                clearWizardState(activeMatchId)
                setCurrentView('match')
              } else {
                saveWizardPhase(activeMatchId, 'team-setup')
                setCurrentView('team-setup')
              }
            }}
            onContinue={() => {
              logger.info('EventBuyIn onContinue', { matchType: activeMatch?.match_type, status: activeMatch?.status, isEditing: isEditingSetup })
              if (isEditingSetup) {
                setIsEditingSetup(false)
                hasRedirectedRef.current = true
                buyInCompletedRef.current = true
                clearWizardState(activeMatchId)
                setCurrentView('match')
              } else if (activeMatch?.match_type === 'invitational' || activeMatch?.match_type === 'trip') {
                // Activating for the first time
                hasRedirectedRef.current = true
                buyInCompletedRef.current = true
                clearWizardState(activeMatchId)
                setCurrentView('match')
              } else {
                saveWizardPhase(activeMatchId, 'session-wizard')
                setCurrentView('match')
                setOpenSessionWizard(true)
                setSessionWizardReturnView('event-buy-in')
              }
            }}
          />
        )
      case 'rounds':
        return <ActiveGame matchId={activeMatchId} onViewGame={handleSelectGame} currentHole={scorecardHole} onHoleChange={setScorecardHole} onBackToMatch={() => { setViewedSessionId(null); setViewedGameId(null); setCurrentView('match') }} onBackToHome={handleGoHome} viewedSessionId={viewedSessionId} onViewSession={(sessionId) => setViewedSessionId(sessionId)} viewedGameId={viewedGameId} onViewGameId={(id) => setViewedGameId(id)} endRoundRequested={endRoundRequested} onEndRoundHandled={() => setEndRoundRequested(false)} />
      case 'game-detail':
        return <GameDetail gameId={selectedGameId} matchId={activeMatchId} onBack={handleBackFromDetail} initialView={gameDetailInitialView} />
      case 'leaderboard':
        return <Leaderboard matchId={activeMatchId} selectedSessionId={viewedSessionId} onClearSession={() => setViewedSessionId(null)} viewedGameId={viewedGameId} />
      case 'history':
        return <GameHistory onSelectGame={handleSelectGame} />
      case 'quick-round-setup':
        return (
          <QuickRoundSetup
            matchId={activeMatchId}
            onBack={handleBackToMatches}
            onRoundCreated={() => {
              // Single Round → go straight to scoring; Mini Match/Golf Trip → go to dashboard
              if (activeMatch?.match_type === 'quick_round') {
                handleStartRound()
              } else {
                setCurrentView('match')
              }
            }}
            onContinueToTeamSetup={() => setCurrentView('team-setup')}
            onContinueToDashboard={() => setCurrentView('match')}
          />
        )
      case 'add-players':
        return <AddPlayersView matchId={activeMatchId} onBack={() => setCurrentView('match')} />
      case 'players':
        if (!isAdmin) {
          setCurrentView('match')
          return null
        }
        return <Players onBack={() => setCurrentView('match')} />
      case 'money':
        return <MoneyBoard matchId={activeMatchId} viewedSessionId={viewedSessionId} viewedGameId={viewedGameId} onSettleUp={() => setCurrentView('settle-up')} />
      case 'settle-up':
        return <SettleUp matchId={activeMatchId} onBack={() => setCurrentView('money')} />
      case 'settlements':
        return <Settlements />
      case 'profile':
        return <PlayerProfile playerId={profilePlayerId} onBack={() => { setCurrentView(previousView || 'match'); setPreviousView(null) }} onManageAccount={() => setCurrentView('manage-account')} />
      case 'manage-account':
        return <ManageAccount onBack={() => setCurrentView('profile')} />
      case 'follows':
        return <FollowsList onBack={() => { setCurrentView(sidebarReturnView || 'match'); setSidebarReturnView(null) }} onViewProfile={handleViewProfile} />
      case 'groups':
        return <GroupsList onBack={() => { setCurrentView(sidebarReturnView || 'match'); setSidebarReturnView(null) }} onViewGroup={(id, isNew) => { setViewedGroupId(id); setIsNewGroup(!!isNew); setCurrentView('group-detail') }} />
      case 'group-detail':
        return <GroupDetail groupId={viewedGroupId} isNew={isNewGroup} onBack={() => { setViewedGroupId(null); setIsNewGroup(false); setCurrentView('groups') }} />
      default:
        return <HomePage onReady={() => setHomeReady(true)} />
    }
  }

  const playerName = myPlayer?.display_name
  const playerColor = myPlayer?.avatar_color
  const playerHandicap = myPlayer?.handicap_index
  const meta = user?.user_metadata || {}
  const userDisplayName = playerName || meta.display_name || meta.full_name || meta.name || user?.email?.split('@')[0] || 'User'
  const userInitials = (myPlayer?.first_name && myPlayer?.last_name)
    ? (myPlayer.first_name.charAt(0) + myPlayer.last_name.charAt(0)).toUpperCase()
    : userDisplayName.charAt(0).toUpperCase()

  // data-app-state lets Watchdog L distinguish a real rendered view from a
  // blank/loading state without walking the React tree.
  const appStateAttr = showLoadingOverlay ? 'overlay' : 'rendered'

  return (
    <div className="app" data-app-state={appStateAttr} data-active-view={currentView}>
      {/* Full-screen loading overlay while HomePage data loads */}
      {showLoadingOverlay && <LoadingScreen overlay />}

      {/* Sidebar overlay */}
      {showSidebar && <div className="sidebar-overlay" onClick={() => setShowSidebar(false)} />}

      {/* Sidebar */}
      <aside ref={sidebarRef} className={`sidebar ${showSidebar ? 'open' : ''}`}>
        <div className="sidebar-header">
          <button className="header-btn" onClick={() => setShowSidebar(false)}>
            <X size={20} />
          </button>
        </div>
        <div className="sidebar-profile" onClick={() => { setSidebarReturnView(currentView); handleViewProfile(null); setShowSidebar(false) }} style={{ cursor: 'pointer' }}>
          <div className="sidebar-avatar" style={playerColor ? { backgroundColor: playerColor } : {}}>
            {userInitials}
          </div>
          <div className="sidebar-profile-info">
            <span className="sidebar-profile-name">{userDisplayName}</span>
            <span className="sidebar-profile-email">{user?.email}</span>
            {playerHandicap != null && (
              <span className="sidebar-profile-handicap">HCP Index: {playerHandicap}</span>
            )}
          </div>
        </div>
        <div className="sidebar-section">
          <button
            className="theme-option"
            onClick={() => { setSidebarReturnView(currentView); handleViewProfile(null); setShowSidebar(false) }}
          >
            <User size={16} />
            My Profile
          </button>
          {profileIncomplete && (
            <button
              className="theme-option"
              onClick={() => { setSidebarReturnView(currentView); handleViewProfile(null); setShowSidebar(false) }}
              style={{ paddingTop: 0, marginTop: '-0.5rem' }}
            >
              <span className="profile-badge">Complete Profile</span>
            </button>
          )}
          <button
            className="theme-option"
            onClick={() => { setSidebarReturnView(currentView); setCurrentView('follows'); setShowSidebar(false) }}
          >
            <UserStar size={16} />
            Friends
          </button>
          <button
            className="theme-option"
            onClick={() => { setSidebarReturnView(currentView); setCurrentView('groups'); setShowSidebar(false) }}
          >
            <Users size={16} />
            Groups
          </button>
        </div>
        <div className="sidebar-section">
          <span className="sidebar-section-label">Support</span>
          <button
            className="theme-option"
            onClick={() => { setShowFeedbackModal(true); setShowSidebar(false) }}
          >
            <MessageSquare size={16} />
            Send Feedback
          </button>
          <button
            className="theme-option"
            onClick={() => { openExternalUrl(DISCORD_INVITE); setShowSidebar(false) }}
          >
            <DiscordIcon size={16} />
            Join Discord
          </button>
        </div>
        <div className="sidebar-footer">
          <span className="sidebar-version">v{__APP_VERSION__}</span>
          <button className="sidebar-logout" onClick={signOut}>
            <LogOut size={18} />
            Sign Out
          </button>
        </div>
      </aside>

      <header className="app-header" ref={appHeaderRef}>
        <div className="app-header-row">
          <div className="app-header-left">
            <button className="header-btn hamburger-btn" onClick={() => { if (appReadyRef.current) setShowSidebar(true) }} style={{ position: 'relative' }}>
              <Menu size={20} />
              {profileIncomplete && <span className="notification-dot" />}
            </button>
            <img src={logoIcon} alt="" className="app-brand-icon" />
            <span className="app-brand">Press<span className="app-brand-alt">2DN</span></span>
          </div>
          <div className="app-header-user">
            <span className="user-name">{userDisplayName}</span>
            <div className="user-avatar" style={playerColor ? { backgroundColor: playerColor } : {}}>{userInitials}</div>
          </div>
        </div>
      </header>

      {sessionExpired && (
        <div style={{
          padding: '0.75rem 1rem',
          backgroundColor: '#dc2626',
          color: '#fff',
          textAlign: 'center',
          fontSize: '0.85rem',
          fontWeight: 600,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '0.75rem',
        }}>
          <span>Session expired. Please sign in again.</span>
          <button
            onClick={() => { dismissSessionExpired(); signOut(); }}
            style={{
              padding: '0.35rem 0.75rem',
              borderRadius: '0.375rem',
              border: '1px solid rgba(255,255,255,0.4)',
              backgroundColor: 'transparent',
              color: '#fff',
              fontWeight: 600,
              cursor: 'pointer',
              fontSize: '0.8rem',
            }}
          >
            Sign In
          </button>
        </div>
      )}

      {/* Email verification banner — soft reminder for unverified email-based users */}
      {user && !user.email_confirmed_at && user.app_metadata?.provider === 'email' && (
        <div style={{
          padding: '0.625rem 1rem',
          backgroundColor: 'rgba(245, 158, 11, 0.12)',
          borderBottom: '1px solid rgba(245, 158, 11, 0.25)',
          textAlign: 'center',
          fontSize: '0.8125rem',
          color: 'var(--text-secondary)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '0.5rem',
          flexWrap: 'wrap',
        }}>
          <Mail size={14} style={{ color: '#f59e0b', flexShrink: 0 }} />
          <span>Verify your email to secure your account.</span>
          <button
            onClick={() => supabase.auth.resend({ type: 'signup', email: user.email })}
            style={{
              background: 'none',
              border: 'none',
              color: 'var(--accent-primary)',
              fontWeight: 600,
              cursor: 'pointer',
              fontSize: '0.8125rem',
              padding: 0,
            }}
          >
            Resend email
          </button>
        </div>
      )}


      {showFeedbackModal && (
        <FeedbackModal
          context={buildFeedbackContext()}
          onClose={() => setShowFeedbackModal(false)}
        />
      )}

      {showWelcome && (
        <WelcomeModal
          onClose={() => setShowWelcome(false)}
          onOpenDiscord={() => openExternalUrl(DISCORD_INVITE)}
        />
      )}

      {updateRecommended && (
        <UpdateModal storeUrl={storeUrl} onDismiss={dismissRecommendation} />
      )}

      {activeMatchId && hasActiveRound && <Ticker matchId={activeMatchId} />}
      {activeMatchId && hasActiveRound && <ToastAlerts matchId={activeMatchId} />}

      <main className="main-content">
        <ErrorBoundary>
          {renderView()}
        </ErrorBoundary>
      </main>

      {!activeMatchId && currentView === 'match' && <MainNav activeTab={mainTab} onNavigate={setMainTab} />}
      {activeMatchId && currentView !== 'quick-round-setup' && currentView !== 'team-setup' && currentView !== 'event-buy-in' && activeMatch?.status !== 'setup' && !dashboardWizardOpen && <BottomTabs currentView={currentView} onNavigate={(v) => { if (v === 'match') { setViewedGameId(null) } setCurrentView(v) }} matchType={activeMatch?.match_type} />}
    </div>
  )
}

export default App
